import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

/** Vista de una invitación de pagador (sin secretos). */
export interface PayerInvitation {
  id: string;
  shortCode: string;
  pais: 'CL' | 'VE';
  tipoUsuarioId: number; // 0 persona, 1 empresa
  status:
    | 'PENDING'
    | 'OTP_VERIFIED'
    | 'PROCESSING'
    | 'COMPLETED'
    | 'REJECTED'
    | 'EXPIRED'
    | 'REVOKED';
  /** RUT/cédula cargado al generar el link (backends anteriores no lo mandan). */
  numeroId?: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  pagadorId: string | null;
  rejectionReason: string | null;
  expiresAt: string;
  completedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
  /** URL del link (con token). Presente solo si el link sigue vigente/compartible. */
  url?: string;
  /** Registro que inició el pagador desde el link permanente del cliente. */
  linkPermanente?: boolean;
}

/** Link permanente del cliente (uno por cliente). */
export interface PayerLinkView {
  code: string | null;
  url: string | null;
  /** Recibe registros ahora. */
  activo: boolean;
  motivo: 'CUENTA_NO_HABILITADA' | 'PAUSADO_POR_REVISION' | null;
  pendientes: number;
  pendientesMax: number;
  reglas: { registrosPorDia: number; documentosPorContacto: number; otpPorDia: number };
  createdAt: string | null;
}

/** Respuesta al crear/reenviar: `url` siempre presente. */
export interface PayerInvitationCreated extends PayerInvitation {
  url: string;
}

export interface CreateInvitationBody {
  pais: 'CL' | 'VE';
  tipoUsuarioId: number;
  rut: string;
  contactEmail?: string;
  contactPhone?: string;
}

/**
 * Gestión de links de invitación de pagadores (cliente autenticado).
 * El token de Cognito lo inyecta el `ApiRestInterceptor` global; acá no se
 * arma el header Authorization a mano.
 */
@Injectable({ providedIn: 'root' })
export class PayerInvitationService {
  private readonly base = `${environment.apiNest}payer-invitations`;
  private readonly baseLink = `${environment.apiNest}payer-link`;

  constructor(private http: HttpClient) {}

  create(body: CreateInvitationBody): Promise<PayerInvitationCreated> {
    return firstValueFrom(
      this.http.post<PayerInvitationCreated>(this.base, body),
    );
  }

  list(): Promise<PayerInvitation[]> {
    return firstValueFrom(this.http.get<PayerInvitation[]>(this.base));
  }

  revoke(id: string): Promise<PayerInvitation> {
    return firstValueFrom(
      this.http.post<PayerInvitation>(`${this.base}/${id}/revoke`, {}),
    );
  }

  /** Mi link permanente (el backend lo crea si no existe y la cuenta está habilitada). */
  getLink(): Promise<PayerLinkView> {
    return firstValueFrom(this.http.get<PayerLinkView>(this.baseLink));
  }

  /**
   * ¿El cliente puede ver y usar pagadores? (permiso + APROBADO, en vivo).
   * Va con `X-Skip-Error-Interceptor`: un 401 acá solo esconde pagadores, no
   * tiene que cerrar la sesión.
   */
  getAcceso(): Promise<{ habilitado: boolean }> {
    return firstValueFrom(
      this.http.get<{ habilitado: boolean }>(`${this.baseLink}/acceso`, {
        headers: { 'X-Skip-Error-Interceptor': 'true' },
      }),
    );
  }

  /** Regenera el link: el anterior deja de funcionar. */
  rotateLink(): Promise<PayerLinkView> {
    return firstValueFrom(this.http.post<PayerLinkView>(`${this.baseLink}/rotate`, {}));
  }

  resend(id: string): Promise<PayerInvitationCreated> {
    return firstValueFrom(
      this.http.post<PayerInvitationCreated>(`${this.base}/${id}/resend`, {}),
    );
  }
}
